import { useState } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button, FloatInput } from "@/components/ds";
import {
  HS_NEWSLETTER_FORM_ID,
  submitHubspotForm,
} from "@/lib/hubspotForms";

interface Props {
  /** Identifies origin (e.g. "blog_sidebar", "insights_footer"). Sent as pageName to HubSpot. */
  source: string;
  /** Optional className for the wrapper. */
  className?: string;
}

/**
 * Newsletter form 100% custom que envia direto para a HubSpot Forms API
 * (portal 5455473 / form d0c2e538-…). Usa os internal names oficiais:
 * firstname, lastname, email.
 */
export function HubspotNewsletterForm({ source, className }: Props) {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setStatus("error");
      setErrorMsg("Informe um e-mail válido.");
      return;
    }
    if (!firstname.trim()) {
      setStatus("error");
      setErrorMsg("Informe seu nome.");
      return;
    }

    setStatus("loading");
    try {
      await submitHubspotForm({
        formId: HS_NEWSLETTER_FORM_ID,
        pageName: source,
        fields: [
          { name: "firstname", value: firstname.trim() },
          { name: "lastname", value: lastname.trim() },
          { name: "email", value: email.trim() },
        ],
      });
      setStatus("ok");
    } catch (err) {
      console.error("[hubspot newsletter]", err);
      setStatus("error");
      setErrorMsg("Não conseguimos concluir sua inscrição. Tente novamente.");
    }
  }

  if (status === "ok") {
    return (
      <div
        className={`rounded-xl bg-card border border-foreground/[0.06] p-5 flex items-start gap-3 ${className ?? ""}`}
      >
        <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
        <div>
          <div className="font-display text-[15px] font-medium text-foreground">
            Inscrição confirmada
          </div>
          <p className="text-[13px] text-foreground/65 mt-0.5">
            Obrigado! Em breve você começa a receber nossa newsletter.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={`space-y-3 ${className ?? ""}`}>
      <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
        <FloatInput
          label="Nome"
          value={firstname}
          onChange={(e) => setFirstname(e.target.value)}
          required
        />
        <FloatInput
          label="Sobrenome"
          value={lastname}
          onChange={(e) => setLastname(e.target.value)}
        />
      </div>
      <FloatInput
        label="E-mail corporativo"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <Button
        type="submit"
        variant="primary"
        size="md"
        iconRight={<ArrowRight />}
        className="w-full"
        disabled={status === "loading"}
      >
        {status === "loading" ? "Enviando..." : "Assinar newsletter"}
      </Button>
      {status === "error" && errorMsg && (
        <p className="text-xs text-foreground/65 text-center">{errorMsg}</p>
      )}
      <p className="text-[11px] text-foreground/60 text-center">
        Ao assinar você concorda com nossa política de privacidade.
      </p>
    </form>
  );
}
